import React, { useCallback, useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { Card } from '@/components/ui/card';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Bot } from 'lucide-react';
import { AiDrafts } from '@/components/ai/AiDrafts';
import { KnowledgeBase } from '@/components/ai/KnowledgeBase';
import { AiPlayground } from '@/components/ai/AiPlayground';
import { AiSettings } from '@/components/ai/AiSettings';
import { AiMetrics } from '@/components/ai/AiMetrics';

const STATS = [
  ['draft', 'Borradores pendientes (tickets)'],
  ['auto_sent', 'Tickets auto-respondidos'],
  ['sent', 'Tickets aprobados por agente'],
  ['kb_articles', 'Artículos activos'],
];

export default function AiAgentPage() {
  const { user } = useAuth();
  const canAct = ['admin', 'tech'].includes(user?.role);
  const [stats, setStats] = useState({});
  const [settings, setSettings] = useState(null);

  const loadStats = useCallback(() => { api.get('/ai/stats').then(({ data }) => setStats(data)).catch(() => {}); }, []);
  useEffect(() => {
    loadStats();
    api.get('/ai/settings').then(({ data }) => setSettings(data)).catch(() => {});
  }, [loadStats]);

  return (
    <div className="space-y-6" data-testid="ai-agent-page">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2"><Bot className="w-6 h-6 text-[hsl(var(--primary))]" />Agente IA de Soporte</h1>
        <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1">
          Responde los tickets sencillos y prepara borradores para el resto.
          {settings && <> Estado: <span className={settings.auto_enabled ? 'text-emerald-300' : 'text-slate-400'} data-testid="ai-agent-state">{settings.auto_enabled ? (settings.auto_reply_enabled ? 'activo · híbrido' : 'activo · solo borradores') : 'pausado'}</span></>}
        </p>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {STATS.map(([k, label]) => (
          <Card key={k} className="p-4" data-testid={`ai-stat-${k}`}>
            <div className="text-xs text-[hsl(var(--muted-foreground))]">{label}</div>
            <div className="text-2xl font-bold mt-1">{stats[k] ?? '—'}</div>
          </Card>
        ))}
      </div>
      <Tabs defaultValue="drafts">
        <TabsList data-testid="ai-tabs">
          <TabsTrigger value="drafts" data-testid="ai-tab-drafts">Bandeja IA</TabsTrigger>
          <TabsTrigger value="metrics" data-testid="ai-tab-metrics">Métricas</TabsTrigger>
          <TabsTrigger value="kb" data-testid="ai-tab-kb">Base de conocimiento</TabsTrigger>
          {canAct && <TabsTrigger value="test" data-testid="ai-tab-test">Probar agente</TabsTrigger>}
          <TabsTrigger value="settings" data-testid="ai-tab-settings">Ajustes</TabsTrigger>
        </TabsList>
        <TabsContent value="drafts" className="mt-4"><AiDrafts canAct={canAct} onChange={loadStats} /></TabsContent>
        <TabsContent value="metrics" className="mt-4"><AiMetrics /></TabsContent>
        <TabsContent value="kb" className="mt-4"><KnowledgeBase canAct={canAct} onChange={loadStats} /></TabsContent>
        {canAct && <TabsContent value="test" className="mt-4"><AiPlayground threshold={settings?.confidence_threshold ?? 0.85} /></TabsContent>}
        <TabsContent value="settings" className="mt-4"><AiSettings settings={settings} isAdmin={user?.role === 'admin'} onSaved={setSettings} /></TabsContent>
      </Tabs>
    </div>
  );
}
